import { useState } from "react";
import { FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { apiError, downloadFile } from "@/lib/api";

/** One use case, as a report fit to carry into a meeting — Word or PDF, built on the spot. */
export const ProjectReportButton = ({ project, testId }) => {
  const [busy, setBusy] = useState("");
  const id = testId || `project-report-${project.code}`;

  const take = async (format) => {
    setBusy(format);
    try {
      await downloadFile(
        `/projects/${project.id}/report`,
        { format },
        `${project.code}.${format}`,
      );
      toast.success(`${project.code} report ready as ${format.toUpperCase()}.`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={id}
          title="Take out this use case as a report"
          onClick={(e) => e.stopPropagation()}
          className="flex h-8 items-center gap-1 border border-border bg-card px-2 font-display text-[9px] font-bold uppercase tracking-widest transition-colors duration-200 hover:bg-muted"
        >
          {busy ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <FileText className="h-3 w-3" />
          )}
          Report
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-52 rounded-none bg-popover p-0"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-border px-3 py-2">
          <div className="label-caps">{project.code}</div>
          <p className="mt-0.5 text-[10px] text-muted-foreground">
            One to two pages — where it stands, its figures and its milestone dates.
          </p>
        </div>
        {[
          ["docx", "Word (.docx)"],
          ["pdf", "PDF"],
        ].map(([format, label]) => (
          <button
            key={format}
            type="button"
            data-testid={`${id}-${format}`}
            disabled={!!busy}
            onClick={() => take(format)}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors duration-200 hover:bg-muted disabled:opacity-50"
          >
            <FileText className="h-3.5 w-3.5" /> {label}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
};
